// @flow
import React, { useEffect } from 'react';
import { StyleSheet, AppState } from 'react-native';
import { ImportantNotice, Separator } from 'trpc-react-native-ui';
import theme from '../../../theme-config';
import type { PaymentAuthentication } from '../../../modules/Payment/reducer';
import { FullButton } from '../../common';
import I18n from '../../../lib/i18n';
import formatAmount from '../../../lib/formatAmount';
import { dateUtils } from '../../../lib/date-utils';

const styles = StyleSheet.create({
  button: {
    alignSelf: 'center',
    marginTop: 20,
    maxWidth: '100%',
    paddingHorizontal: 20,
  },
  emphasizedText: {
    color: theme.errorColor,
    fontWeight: theme.ui.font.weight.bold,
  },
});

type PaymentAuthenticationsOwnProps = {
  withSeparator: boolean,
};

export type StateProps = {
  paymentAuthentications: PaymentAuthentication[],
};
export type DispatchProps = {
  fetchPaymentAuthentications: Function,
  navigateToPaymentAuthorizationResult: Function,
};

export type PaymentAuthenticationsProps = PaymentAuthenticationsOwnProps & StateProps & DispatchProps;

type AppStateType = 'active' | 'background' | 'inactive';

const PaymentAuthentications = ({
  fetchPaymentAuthentications,
  navigateToPaymentAuthorizationResult,
  paymentAuthentications,
  withSeparator = true,
}: PaymentAuthenticationsProps) => {
  useEffect(() => {
    fetchPaymentAuthentications();
  }, []);

  const handleAppStateChange = (newAppState: AppStateType) => {
    if (newAppState === 'active') {
      fetchPaymentAuthentications();
    }
  };

  useEffect(() => {
    const event = AppState.addEventListener('change', handleAppStateChange);
    return () => {
      event.remove();
    };
  }, []);

  const renderImportantNotice = (paymentAuthentication: PaymentAuthentication) => {
    const {
      amount,
      notificationMaxSendDate,
      notificationOuid,
      primaryResourceValue,
      processName,
      stripeIntentId,
      stripeRedirectUrl,
    } = paymentAuthentication;

    const redirectToPaymentAuthorizationResultPage = () => {
      navigateToPaymentAuthorizationResult({
        notificationMaxSendDate,
        notificationOuid,
        processName,
        stripeIntentId,
        stripeRedirectUrl,
      });
    };

    const getDescriptionTranslation = () => {
      const lineSpecificDescription = primaryResourceValue
        ? I18n.t('Dashboard.Payments.paymentAuthentications.lineSpecificDescription', {
            primaryResourceValue,
          })
        : '';

      const renewalSpecificDescription =
        processName === 'renewalProcess'
          ? I18n.t('Dashboard.Payments.paymentAuthentications.renewalSpecificDescription', {
              date: dateUtils.format(notificationMaxSendDate, 'PPPp'),
            })
          : '';

      return I18n.t('Dashboard.Payments.paymentAuthentications.description', {
        amount: formatAmount(amount),
        lineSpecificDescription,
        renewalSpecificDescription,
      });
    };

    return (
      <>
        <ImportantNotice emphasizedText={getDescriptionTranslation()} emphasizedTextStyles={styles.emphasizedText}>
          <FullButton
            text={I18n.t('Dashboard.Payments.paymentAuthentications.buttonText')}
            onPress={redirectToPaymentAuthorizationResultPage}
            style={styles.button}
          />
        </ImportantNotice>
        {withSeparator && <Separator crossAxisMargin={-3} thickness={4} />}
      </>
    );
  };

  return (
    <>
      {paymentAuthentications.map((paymentAuthentication: PaymentAuthentication) =>
        renderImportantNotice(paymentAuthentication)
      )}
    </>
  );
};

export default PaymentAuthentications;
